import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChipInput } from "../../../hooks/useChipInput";
import { useDataStudioColumnDetails } from "../../../hooks/useDataStudioColumnDetails";
import type {
  GetDataStudioColumnDetailsResponse,
  UpdateDataStudioColumnDetailsRequest,
} from "../../../services/dataStudio/dataStudioColumnDetails.types";
import type { ConnectorColumnIdentity } from "../../../services/dataStudio/dataStudioAssets.types";

interface UseDataStudioColumnDetailsModalInternalStateArgs
  extends ConnectorColumnIdentity {
  open: boolean;
  onClose: () => void;
  onUpdated?: (details: GetDataStudioColumnDetailsResponse) => void;
}

/** Order-sensitive list comparison — the API replaces a list wholesale. */
const sameList = (left: string[], right: string[]): boolean =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export const useDataStudioColumnDetailsModalInternalState = ({
  open,
  connectionId,
  assetId,
  columnId,
  onClose,
  onUpdated,
}: UseDataStudioColumnDetailsModalInternalStateArgs) => {
  const identity = useMemo(
    () => ({ connectionId, assetId, columnId }),
    [connectionId, assetId, columnId],
  );
  const detailsQuery = useDataStudioColumnDetails(identity, open);
  const details = detailsQuery.data;
  const resetUpdate = detailsQuery.resetUpdate;
  const [description, setDescription] = useState("");
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const hydratedDetailsKey = useRef<string | null>(null);

  /**
   * Rehydrates the form from the server's view.
   *
   * The API has no version token to key this on, so the key is the identity
   * plus the server values themselves. That still re-seeds the form after a
   * successful PATCH writes a new response into the cache, without wiping the
   * user's in-progress edits on an unrelated re-render.
   */
  useEffect(() => {
    if (!open) {
      hydratedDetailsKey.current = null;
      return;
    }
    if (!details) return;
    const detailsKey = JSON.stringify([
      connectionId,
      assetId,
      columnId,
      details.description,
      details.synonyms,
      details.notes,
    ]);
    if (hydratedDetailsKey.current === detailsKey) return;
    hydratedDetailsKey.current = detailsKey;
    setDescription(details.description);
    setSynonyms([...details.synonyms]);
    setNotes([...details.notes]);
    setNoteDraft("");
    resetUpdate();
  }, [open, connectionId, assetId, columnId, details, resetUpdate]);

  const addSynonym = useCallback((value: string) => {
    setSynonyms((current) =>
      current.some(
        (synonym) => synonym.toLocaleLowerCase() === value.toLocaleLowerCase(),
      )
        ? current
        : [...current, value],
    );
  }, []);
  const removeSynonym = useCallback((value: string) => {
    setSynonyms((current) => current.filter((synonym) => synonym !== value));
  }, []);
  const synonymInput = useChipInput({
    values: synonyms,
    onAdd: addSynonym,
    onRemove: removeSynonym,
    validate: (value) => value.length <= 100,
  });
  const handleNoteChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNoteDraft(event.target.value);
    },
    [],
  );
  const handleNoteKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      const note = event.currentTarget.value.trim();
      if (!note) return;
      setNotes((current) => [...current, note]);
      setNoteDraft("");
    },
    [],
  );
  const removeNote = useCallback((index: number) => {
    setNotes((current) => current.filter((_, noteIndex) => noteIndex !== index));
  }, []);

  /**
   * The curated fields the user actually changed.
   *
   * This is what makes the PATCH sparse. The API leaves an omitted field
   * alone, so sending only what was touched avoids clobbering a field someone
   * else edited between load and save — and an empty object is exactly the
   * body the API rejects with a 400, which is why it also drives `canUpdate`.
   */
  const changedFields = useMemo(() => {
    if (!details) return {};
    const changes: Pick<
      UpdateDataStudioColumnDetailsRequest,
      "description" | "notes" | "synonyms"
    > = {};
    const trimmedDescription = description.trim();
    if (trimmedDescription !== details.description) {
      changes.description = trimmedDescription;
    }
    if (!sameList(synonyms, details.synonyms)) changes.synonyms = synonyms;
    if (!sameList(notes, details.notes)) changes.notes = notes;
    return changes;
  }, [details, description, synonyms, notes]);

  const isDirty = Object.keys(changedFields).length > 0;
  const canUpdate = Boolean(details && isDirty && !detailsQuery.isUpdating);

  const close = () => {
    if (detailsQuery.isUpdating) return;
    detailsQuery.resetUpdate();
    onClose();
  };

  const submit = () => {
    if (!details || !canUpdate) return;
    detailsQuery.update({ ...identity, ...changedFields }, (response) => {
      onUpdated?.(response);
      onClose();
    });
  };

  return {
    ...detailsQuery,
    description,
    setDescription,
    synonyms,
    synonymInput,
    removeSynonym,
    notes,
    noteInput: {
      draft: noteDraft,
      error: false,
      handleChange: handleNoteChange,
      handleKeyDown: handleNoteKeyDown,
    },
    removeNote,
    changedFields,
    canUpdate,
    close,
    submit,
  };
};
